import { useEffect, useReducer } from 'react';
import { poll, PollStatus } from '../utils/polling';

interface Action {
  type: string;
  payload?: any;
}

interface PollingProps {
  promiseFn: () => void; // Promise to be resolved on each poll - API / Promise
  pollingInterval: number; // In milliseconds
  dataFormatter?: (data: any) => { data: any };
}
const initialState = {
  data: undefined,
  error: undefined,
  status: PollStatus.LOADING
};

const reducer = (state: any, action: Action) => {
  switch (action.type) {
    case PollStatus.LOADING:
      return { ...state, status: PollStatus.LOADING };

    case PollStatus.SUCCESS:
      return {
        ...state,
        status: PollStatus.SUCCESS,
        data: action.payload,
        error: undefined
      };

    case PollStatus.ERROR:
      return {
        ...state,
        status: PollStatus.ERROR,
        data: undefined,
        error: action.payload
      };

    case PollStatus.RESET:
      return { ...initialState };

    case PollStatus.STOP:
      return { ...initialState, status: PollStatus.STOP, data: state.data };

    default:
      throw new Error('invalid action');
  }
};

const actions = {
  reset: (dispatch: any) =>
    dispatch({
      type: PollStatus.RESET
    }),
  stop: (dispatch: any) =>
    dispatch({
      type: PollStatus.STOP
    })
};

export default function usePolling(props: PollingProps) {
  const { promiseFn, pollingInterval, dataFormatter = (res: any) => res } = props;
  const [state, dispatch] = useReducer(reducer, initialState);

  useEffect(() => {
    const callback = (status: PollStatus, response: any) => {
      switch (status) {
        case PollStatus.STOP:
          return actions.stop(dispatch);
        case PollStatus.LOADING:
          return dispatch({ type: status });
        case PollStatus.SUCCESS:
        case PollStatus.ERROR: {
          return dispatch({ type: status, payload: response });
        }
        default:
          return;
      }
    };

    const unsubscribe: () => void = poll(promiseFn, dataFormatter, pollingInterval, callback);

    return () => {
      unsubscribe();
    };
  }, []);

  return state;
}

// Temp solution to be deleted once FCM token is fixed
export function usePollingForNotificationsOnly(props: PollingProps) {
  const { promiseFn, pollingInterval, dataFormatter = (res: any) => res } = props;
  const [state, dispatch] = useReducer(reducer, initialState);

  useEffect(() => {
    const callback = (status: PollStatus, response: any) => {
      switch (status) {
        case PollStatus.STOP:
          return actions.stop(dispatch);
        case PollStatus.LOADING:
          return dispatch({ type: status });
        case PollStatus.SUCCESS:
        case PollStatus.ERROR: {
          return dispatch({ type: status, payload: response });
        }
        default:
          return;
      }
    };

    const unsubscribe: () => void = poll(promiseFn, dataFormatter, pollingInterval, callback);

    return () => {
      unsubscribe();
    };
  }, [pollingInterval]);

  return state;
}
